import {
  useAnimations,
  useGLTF,
  useTexture,
  Trail,
  SpriteAnimator,
  useSpriteLoader,
} from "@react-three/drei";
import { useControls } from "leva";
import { Suspense, useEffect, useRef, useMemo, useState } from "react";
import * as THREE from "three";
import { BallCollider, RapierCollider, vec3 } from "@react-three/rapier";
import { useFrame } from "@react-three/fiber";
import { useGame } from "../../src/stores/useGame";
import { useDialogs } from "../stores/useDialogs";
import React from "react";
import {
  OUTLINE_ENABLED,
  OUTLINE_COLOR,
  CHARACTER_OUTLINE_THICKNESS,
  CHARACTER_OUTLINE_OPACITY,
} from "../config/game";
import { computeSmoothedNormals } from "../utils/computeSmoothedNormals";


export default function NewCharacterModel(props: any) {
  // Reference to the model group
  const group = useRef<THREE.Group | null>(null);
  
  // Load the new model
  const model = useGLTF("/Surfer002.glb");
  const { nodes, animations, meshes } = model;

  useEffect(() => {
    console.log('NEW CHARACTER MODEL', model);
  }, [model]);

  const { actions } = useAnimations(animations, group);
  
  // gradientMapTexture for MeshToonMaterial
  const gradientMapTexture = useTexture("./textures/3.jpg");
  gradientMapTexture.minFilter = THREE.NearestFilter;
  gradientMapTexture.magFilter = THREE.NearestFilter;
  gradientMapTexture.generateMipmaps = false;

  /**
   * Prepare hands ref for attack action
   */
  const rightHandRef = useRef<THREE.Group | null>(null);
  const rightHandColliderRef = useRef<THREE.Group | null>(null);
  const leftHandRef = useRef<THREE.Group | null>(null);
  const leftHandColliderRef = useRef<THREE.Group | null>(null);
  const rightHandPos = useMemo(() => new THREE.Vector3(), []);
  const leftHandPos = useMemo(() => new THREE.Vector3(), []);
  const bodyPos = useMemo(() => new THREE.Vector3(), []);
  const bodyRot = useMemo(() => new THREE.Quaternion(), []);
  let rightHand: THREE.Bone | null = null;
  let leftHand: THREE.Bone | null = null;

  /**
   * Prepare punch effect sprite
   */
  const { spriteObj } = useSpriteLoader("./punchEffect.png", null, null, 7)
  const [punchEffectProps, setPunchEffectProp] = useState({
    visible: false,
    scale: [1, 1, 1] as [number, number, number],
    play: false,
    position: [-0.2, -0.2, 0.5] as [number, number, number],
    startFrame: 0,
  });

  /**
   * Debug settings
   */
  const { mainColor, outlineColor, trailColor } = useControls(
    "Character Model",
    {
      mainColor: "mediumslateblue",
      outlineColor: "black",
      trailColor: "violet",
    }
  );

  /**
   * Compute smoothed normals for cleaner outline
   */
  const outlineGeometry = useMemo(() => {
    if (!OUTLINE_ENABLED) return null;
    const geo = (meshes.Retopo_temp_meshply001 as any).geometry as THREE.BufferGeometry;
    if (!geo.getAttribute("smoothNormal")) {
      computeSmoothedNormals(geo);
    }
    return geo;
  }, [meshes]);

  /**
   * Outline material for skinned mesh (uses smoothed normals)
   */
  const outlineMaterial = useMemo(() => {
    if (!OUTLINE_ENABLED) return null;
    return new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        outlineThickness: { value: CHARACTER_OUTLINE_THICKNESS },
        outlineColor: { value: OUTLINE_COLOR },
        outlineOpacity: { value: CHARACTER_OUTLINE_OPACITY },
      },
      vertexShader: `
        #include <common>
        #include <skinning_pars_vertex>

        attribute vec3 smoothNormal;
        uniform float outlineThickness;

        void main() {
          #include <skinbase_vertex>
          #include <beginnormal_vertex>
          objectNormal = smoothNormal;
          #include <skinnormal_vertex>
          #include <begin_vertex>
          #include <skinning_vertex>

          vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
          vec3 viewNormal = normalize(normalMatrix * objectNormal);
          float dist = -mvPosition.z;
          mvPosition.xyz += viewNormal * outlineThickness * dist * 0.001;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;
        uniform float outlineOpacity;
        void main() {
          gl_FragColor = vec4(outlineColor, outlineOpacity);
        }
      `,
    });
  }, []);

  /**
   * Prepare replacing materials
   */
  const meshToonMaterial = useMemo(
    () =>
      new THREE.MeshToonMaterial({
        color: mainColor,
        gradientMap: gradientMapTexture,
        transparent: true,
      }),
    [mainColor]
  );

  /**
   * Character animations setup
   */
  const curAnimation = useGame((state) => state.curAnimation);
  const resetAnimation = useGame((state) => state.reset);
  const initializeAnimationSet = useGame(
    (state) => state.initializeAnimationSet
  );
  const setPlayerPosition = useGame((state) => state.setPlayerPosition);
  const setPlayerRotation = useGame((state) => state.setPlayerRotation);
  const setCameraRotation = useGame((state) => state.setCameraRotation);
  const dialog = useDialogs((state) => state.dialog);
  
  // Map animation names from the new model
  // Note: These should match the actual animation names in your model
 const animationSet = {
    idle: "Idle",
    walk: "Run1",
    run: "Run1",
    jump: "Jump_Start",
    jumpIdle: "Jump_Idle",
    jumpLand: "Jump_Land",
    fall: "Climbing", // This is for falling from high sky
    action1: "Wave",
    action2: "Dance",
    action3: "Cheer",
    action4: "Attack(1h)",
  };

  useEffect(() => {
    // Initialize animation set
    initializeAnimationSet(animationSet);
    
    // Log available animations for debugging
    console.log("Available animations:", animations.map(a => a.name));
  }, []);

  useEffect(() => {
    if (group.current) {
      group.current.traverse((obj) => {
        if ((obj as any).isMesh) {
          obj.castShadow = true;
          obj.receiveShadow = true;
        }
        // Prepare both hands bone object
        if (obj instanceof THREE.Bone) {
          if (obj.name === "RightHand") rightHand = obj;
          if (obj.name === "LeftHand") leftHand = obj;
        }
      });
    }
  });

  useFrame((state) => {
    // Update player position for interaction
    if (group.current) {
      const worldPos = new THREE.Vector3();
      group.current?.getWorldPosition(worldPos);
      setPlayerPosition(worldPos);

      // Update player rotation (Y axis)
      const worldQuat = new THREE.Quaternion();
      group.current?.getWorldQuaternion(worldQuat);
      // Use YXZ order to correctly extract yaw (Y) rotation even if character is tilted
      const euler = new THREE.Euler().setFromQuaternion(worldQuat, 'YXZ');
      setPlayerRotation(euler.y);
    }
    
    const camera = state.camera;
    if (camera) {
        // Get the direction the camera is facing in world space
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        // Project onto XZ plane and calculate angle
        // Math.atan2(x, z) gives angle from +Z axis
        // We want the rotation of the camera around Y axis
        const angle = Math.atan2(direction.x, direction.z);
        setCameraRotation(angle);
    }

    if (curAnimation === animationSet.action4) {
      if (rightHand) {
        rightHand.getWorldPosition(rightHandPos);
        group.current?.getWorldPosition(bodyPos);
        group.current?.getWorldQuaternion(bodyRot);
      }

      // Apply hands position to hand colliders
      if (rightHandColliderRef.current) {
        // check if parent group autobalance is on or off
        if (group.current?.parent?.quaternion.y === 0 && group.current?.parent?.quaternion.w === 1) {
          rightHandRef.current?.position.copy(rightHandPos).sub(bodyPos).applyQuaternion(bodyRot.conjugate());
        } else {
          rightHandRef.current?.position.copy(rightHandPos).sub(bodyPos);
        }
        if (rightHandColliderRef.current && rightHandRef.current) {
          (rightHandColliderRef.current as any).setTranslationWrtParent(
            rightHandRef.current.position as THREE.Vector3
          );
        }
      }
    }
  });

  useEffect(() => {
    // Play animation
    const action = actions[curAnimation ? curAnimation : animationSet.idle] as THREE.AnimationAction | null;
    if (action === null || action === undefined) return;
    console.log('curAnimation', curAnimation);
    // For jump and jump land animation, only play once and clamp when finish
    if (
      curAnimation === animationSet.jump ||
      curAnimation === animationSet.jumpLand ||
      curAnimation === animationSet.action1 ||
      curAnimation === animationSet.action2 ||
      curAnimation === animationSet.action3 ||
      curAnimation === animationSet.action4
    ) {
      console.log('playing animation', curAnimation);
      action
        .reset()
        .fadeIn(0.2)
        .setLoop(THREE.LoopOnce, 0)
        .play();
      action.clampWhenFinished = true;
    } else {
      console.log('playing ELSE animation', curAnimation);
      action.reset().fadeIn(0.2).play();
    }

    // When any action is clamp and finished reset animation
    (action as any)._mixer?.addEventListener("finished", () => resetAnimation());

    return () => {
      // Fade out previous action
      action.fadeOut(0.2);

      // Clean up mixer listener, and empty the _listeners array
      if ((action as any)._mixer) {
        (action as any)._mixer.removeEventListener("finished", () =>
          resetAnimation()
        );
        (action as any)._mixer._listeners = [];
      }

      // Move hand collider back to initial position after action
      if (curAnimation === animationSet.action4) {
        if (rightHandColliderRef.current) {
          (rightHandColliderRef.current as any).setTranslationWrtParent(vec3({ x: 0, y: 0, z: 0 })) as THREE.Vector3
        }
      }
    };
  }, [curAnimation]);

  return (
    <Suspense fallback={<capsuleGeometry args={[0.3, 0.7]} />}>
      {/* Head collider */}
      <BallCollider args={[0.5]} position={[0, 0.45, 0]} />
      
      {/* Right hand collider */}
      <group ref={rightHandRef} />
      <BallCollider
        args={[0.1]}
        ref={rightHandColliderRef as any}
        onCollisionEnter={() => {
          if (curAnimation === animationSet.action4) {
            // Play punch effect
            setPunchEffectProp((prev: any) => ({
              ...prev,
              visible: true,
              play: true,
            }));
          }
        }}
      />

      {/* Left hand collider */}
      <group ref={leftHandRef} />
      <BallCollider args={[0.1]} ref={leftHandColliderRef as any} />
      
      {/* Character model */}
      <group
        ref={group}
        {...props}
        dispose={null}
      >
        <group name="Scene" scale={0.8} position={[0, -0.55, 0]}>
          <group name="Character">
            {/* <skinnedMesh
              name="Retopo_temp_meshply001"
              geometry={(nodes.Retopo_temp_meshply001 as any).geometry}
              material={meshToonMaterial}
              skeleton={(nodes.Retopo_temp_meshply001 as any).skeleton}
              receiveShadow
              castShadow
            /> */}
            <skinnedMesh
              name="Retopo_temp_meshply001"
              geometry={(meshes.Retopo_temp_meshply001 as any).geometry}
              material={meshes.Retopo_temp_meshply001.material}
              // material={meshToonMaterial}s
              skeleton={(meshes.Retopo_temp_meshply001 as any).skeleton}
              receiveShadow
              castShadow
            />
            {outlineMaterial && (
              <skinnedMesh
                geometry={(meshes.Retopo_temp_meshply001 as any).geometry}
                material={outlineMaterial}
                skeleton={(meshes.Retopo_temp_meshply001 as any).skeleton}
                renderOrder={1}
                frustumCulled={false}
              />
            )}
            {/* <Trail
              width={1.5}
              color={trailColor}
              length={1.5}
              attenuation={(width) => width}
            > */}
              <primitive object={nodes.Scene} />
            {/* </Trail> */}
          </group>
        </group>
        {/* <SpriteAnimator
          visible={punchEffectProps.visible}
          scale={punchEffectProps.scale}
          position={punchEffectProps.position}
          startFrame={punchEffectProps.startFrame}
          loop={true}
          onLoopEnd={() => {
            setPunchEffectProp((prev) => ({
              ...prev,
              visible: false,
              play: false,
            }));
          }}
          play={punchEffectProps.play}
          numberOfFrames={7}
          alphaTest={0.01}
          asSprite={true}
          spriteDataset={spriteObj}
        /> */}
      </group>
    </Suspense>
  );
}

// Preload the model
useGLTF.preload("/Surfer002.glb");